/**
 * BilanComparisonSelector Component
 *
 * Dropdown or modal for selecting a second statement to compare.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountingStatementSummary } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BilanComparisonSelectorProps {
  statements: AccountingStatementSummary[];
  excludeId?: string;
  onSelect: (id: string) => void;
  isLoading?: boolean;
}

export const BilanComparisonSelector = ({
  statements,
  excludeId,
  onSelect,
  isLoading,
}: BilanComparisonSelectorProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const filteredStatements = statements.filter((s) => s.id !== excludeId);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading || filteredStatements.length === 0}
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
          'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
          'hover:bg-violet-100 dark:hover:bg-violet-900/50',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <CompareIcon className="w-4 h-4" />
        {t('bilans.compareWith')}
        <ChevronDownIcon className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-20 max-h-60 overflow-y-auto">
            {filteredStatements.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                {t('bilans.noOtherStatements')}
              </div>
            ) : (
              <div className="py-1">
                {filteredStatements.map((statement) => (
                  <button
                    key={statement.id}
                    onClick={() => handleSelect(statement.id)}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {statement.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDate(statement.startDate)} - {formatDate(statement.endDate)}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const CompareIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export default BilanComparisonSelector;
